import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { AzureUnavailable, Card, ErrorState, PageHeader, Spinner, Table, Td, Toggle } from "../components/ui";

interface ProjectDetailData {
  project?: Record<string, unknown> & { id: string | number; name?: string };
  members?: { email?: string; name?: string; full_name?: string; role?: string }[];
  features?: Record<string, boolean>;
  [key: string]: unknown;
}

export default function ProjectDetail() {
  const { id = "" } = useParams();
  const { hasRole } = useAuth();
  const canWrite = hasRole("admin");
  const { data, loading, error, errorStatus, reload } = useApi<ProjectDetailData>(`/projects/${encodeURIComponent(id)}`);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function toggleFeature(key: string, value: boolean) {
    setBusy(true);
    setActionError(null);
    try {
      await api.patch(`/projects/${encodeURIComponent(id)}/features`, { [key]: value });
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;
  if (error) {
    return errorStatus === 502 || errorStatus === 503 ? (
      <AzureUnavailable detail={error} />
    ) : (
      <ErrorState message={error} onRetry={reload} />
    );
  }

  const project = data?.project ?? (data as Record<string, unknown> | null);
  const members = data?.members ?? [];
  const features = data?.features ?? {};

  return (
    <div>
      <PageHeader
        title={String(project?.name ?? id)}
        subtitle="Paid platform project"
        actions={
          <Link to="/projects" className="text-sm text-rail-600 hover:underline">
            ← All projects
          </Link>
        }
      />
      {actionError && <ErrorState message={actionError} />}

      <div className="mt-3 grid gap-4 lg:grid-cols-2">
        <Card title="Details">
          <dl className="space-y-1.5 text-sm">
            {Object.entries(project ?? {})
              .filter(([k, v]) => !["members", "features"].includes(k) && (typeof v !== "object" || v === null))
              .map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <dt className="text-slate-500">{k.replaceAll("_", " ")}</dt>
                  <dd className="text-right font-medium text-slate-800">{String(v ?? "—")}</dd>
                </div>
              ))}
          </dl>
        </Card>

        <Card title="Feature flags">
          {Object.keys(features).length === 0 ? (
            <p className="text-sm text-slate-500">No feature flags reported for this project.</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(features).map(([key, enabled]) => (
                <div key={key} className="flex items-center justify-between text-sm">
                  <span className="font-mono text-xs text-slate-700">{key}</span>
                  <Toggle checked={Boolean(enabled)} disabled={!canWrite || busy} onChange={(v) => toggleFeature(key, v)} />
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="mt-4">
        <Card title="Members">
          {members.length === 0 ? (
            <p className="text-sm text-slate-500">No members reported.</p>
          ) : (
            <Table headers={["Email", "Name", "Role"]}>
              {members.map((m, i) => (
                <tr key={i}>
                  <Td>
                    {m.email ? (
                      <Link to={`/users/${encodeURIComponent(m.email)}`} className="text-rail-600 hover:underline">
                        {m.email}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td>{m.name ?? m.full_name ?? "—"}</Td>
                  <Td>{m.role ?? "—"}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}
